import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { TOURS, TourId, TourStep } from '@/components/onboarding/tourSteps';

type TourStatus = 'active' | 'completed' | 'dismissed';

interface StoredState {
  step: number;
  status: TourStatus;
}

interface OnboardingContextType {
  tourId: TourId;
  steps: TourStep[];
  stepIndex: number;
  isActive: boolean;
  startTour: (tourId?: TourId) => void;
  next: () => void;
  back: () => void;
  dismiss: () => void;
  complete: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// The papers tour keeps its original key so existing users are not shown it again.
const storageKey = (userId: string, tourId: TourId) =>
  tourId === 'papers' ? `refify-onboarding:${userId}` : `refify-onboarding:${userId}:${tourId}`;

function readState(userId: string, tourId: TourId): StoredState | null {
  try {
    const raw = localStorage.getItem(storageKey(userId, tourId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.step === 'number' && (parsed.status === 'active' || parsed.status === 'completed' || parsed.status === 'dismissed')) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeState(userId: string, tourId: TourId, state: StoredState) {
  try {
    localStorage.setItem(storageKey(userId, tourId), JSON.stringify(state));
  } catch {
    // storage full/blocked — tour just won't persist
  }
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [tourId, setTourId] = useState<TourId>('papers');
  const [stepIndex, setStepIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);

  const steps = TOURS[tourId];

  // Load persisted state when the user changes. No stored state for a signed-in
  // user means first visit -> start the tour automatically. The papers tour only
  // runs on the papers pages, where its first steps actually exist.
  useEffect(() => {
    if (!user) {
      setIsActive(false);
      setStepIndex(0);
      return;
    }
    if (!location.pathname.startsWith('/papers')) return;
    const stored = readState(user.id, 'papers');
    if (!stored) {
      setTourId('papers');
      setStepIndex(0);
      setIsActive(true);
      writeState(user.id, 'papers', { step: 0, status: 'active' });
    } else if (stored.status === 'active') {
      setTourId('papers');
      setStepIndex(Math.min(stored.step, TOURS.papers.length - 1));
      setIsActive(true);
    }
  }, [user, location.pathname]);


  // First time the user opens Templates, offer the templates tour — but never
  // interrupt a tour that is already running.
  useEffect(() => {
    if (!user || isActive) return;
    if (!location.pathname.startsWith('/templates')) return;
    if (readState(user.id, 'templates')) return;
    setTourId('templates');
    setStepIndex(0);
    setIsActive(true);
    writeState(user.id, 'templates', { step: 0, status: 'active' });
  }, [user, isActive, location.pathname]);

  const persist = useCallback((step: number, status: TourStatus, id: TourId = tourId) => {
    if (user) writeState(user.id, id, { step, status });
  }, [user, tourId]);

  const startTour = useCallback((id: TourId = 'papers') => {
    setTourId(id);
    setStepIndex(0);
    setIsActive(true);
    persist(0, 'active', id);
  }, [persist]);

  const next = useCallback(() => {
    setStepIndex(prev => {
      const nextStep = prev + 1;
      if (nextStep >= steps.length) {
        setIsActive(false);
        persist(prev, 'completed');
        return prev;
      }
      persist(nextStep, 'active');
      return nextStep;
    });
  }, [persist, steps.length]);

  const back = useCallback(() => {
    setStepIndex(prev => {
      const target = Math.max(0, prev - 1);
      persist(target, 'active');
      return target;
    });
  }, [persist]);

  const dismiss = useCallback(() => {
    setIsActive(false);
    persist(stepIndex, 'dismissed');
  }, [persist, stepIndex]);

  const complete = useCallback(() => {
    setIsActive(false);
    persist(steps.length - 1, 'completed');
  }, [persist, steps.length]);

  return (
    <OnboardingContext.Provider value={{ tourId, steps, stepIndex, isActive, startTour, next, back, dismiss, complete }}>
      {children}
    </OnboardingContext.Provider>
  );
}

const noopOnboarding: OnboardingContextType = {
  tourId: 'papers',
  steps: TOURS.papers,
  stepIndex: 0,
  isActive: false,
  startTour: () => {},
  next: () => {},
  back: () => {},
  dismiss: () => {},
  complete: () => {},
};

export function useOnboarding() {
  // Fall back to a no-op tour instead of throwing, so a missing provider
  // (e.g. during hot reload of a lazy-loaded layout) never blanks the app.
  return useContext(OnboardingContext) ?? noopOnboarding;
}
